import { isDefined, MatSerNumber, NotificationIdentifier } from '@evorait/pace-core';
import { Action, createReducer, on } from '@ngrx/store';
import { FormIdentifier } from '../../types/form-identifier';
import { FormList, FormListEntryState, initialFormListState } from '../../types/form-list';
import { FormsConstants } from '../../types/forms-constants';
import { FormListUtilities } from '../../utilities/form-list-utilities';
import { FormUtilities } from '../../utilities/form-utilities';
import { NotificationUtilities } from '../../utilities/notification-utilities';
import {
    cleanupFormListSuccess,
    loadEquipmentSetSuccess,
    loadFormList,
    loadFormListError,
    loadFormListSuccess,
    setSelectedLanguageInFormList,
} from '../actions/form-list.actions';
import {
    abortFormSuccess,
    cleanupFormSuccess,
    loadAllFormXml,
    loadFormIntoCacheSuccess,
    refreshXMLSuccess,
    submitFormSuccess,
} from '../actions/form.actions';
import { initialNotificationFormListState, NotificationFormListState } from '../state/notification-form-list.state';
import { handOverUnsubmittedFormsSuccess } from '../actions/state.actions';
import FORMTYPE = FormsConstants.FORMTYPE;
import SUBMIT_STATUS = FormsConstants.SUBMIT_STATUS;

function loadFormListReducer(
    state: Readonly<NotificationFormListState>,
    props: { notification: NotificationIdentifier },
): Readonly<NotificationFormListState> {
    const identifier = FormListUtilities.generateListIdentifierFromNotification(props.notification);

    const oldFormState = state[identifier];
    const newFormState = isDefined(oldFormState) ? oldFormState : initialFormListState;
    return {
        ...state,
        [identifier]: {
            ...newFormState,
            isLoading: true,
        },
    };
}

export function loadFormListSuccessReducer(
    state: Readonly<NotificationFormListState>,
    props: { formList: Readonly<FormList> },
): Readonly<NotificationFormListState> {
    const identifier = FormListUtilities.generateListIdentifierFromNotification(props.formList.notification);

    const formListEntries = props.formList.entries;

    const oldEditedEntries = props.formList.entries.filter(
        (filteredEntry) => filteredEntry.isOldVersion && filteredEntry.submitStatus === SUBMIT_STATUS.EDITED,
    );

    // we also need to remove new templates for locally saved forms, to not allow the user to create 2 interims for the same document(with different versions)
    oldEditedEntries.forEach((oldEditedEntry) => {
        const newVersionIndex = formListEntries.findIndex((filteredEntry) =>
            FormUtilities.isNewFormVersion(filteredEntry.id.formId, oldEditedEntry.id.formId),
        );
        if (newVersionIndex > -1) {
            formListEntries.splice(newVersionIndex, 1);
        }
    });

    const editedFormListEntries = formListEntries.map((formListEntry) => {
        const lockedBy = FormUtilities.isFormUnlocked(formListEntry.submitStatus) ? '' : formListEntry.lockedBy;

        return {
            ...formListEntry,
            lockedBy,
        };
    });

    return {
        ...state,
        [identifier]: { ...props.formList, entries: editedFormListEntries, isLoading: false, loaded: true },
    };
}

function loadFormListErrorReducer(
    state: Readonly<NotificationFormListState>,
    props: { notification: NotificationIdentifier },
): Readonly<NotificationFormListState> {
    const identifier = FormListUtilities.generateListIdentifierFromNotification(props.notification);
    return { ...state, [identifier]: { ...initialFormListState } };
}

function loadEquipmentSetSuccessReducer(
    state: Readonly<NotificationFormListState>,
    props: { notificationIdentifier: NotificationIdentifier; equipmentSet: MatSerNumber[] },
): Readonly<NotificationFormListState> {
    const identifier = FormListUtilities.generateListIdentifierFromNotification(props.notificationIdentifier);

    const oldFormState = state[identifier];
    const newFormState = isDefined(oldFormState) ? oldFormState : initialFormListState;

    return { ...state, [identifier]: { ...newFormState, equipmentSet: [...props.equipmentSet] } };
}

function loadFormIntoCacheSuccessReducer(
    state: Readonly<NotificationFormListState>,
    props: { form: FormIdentifier; notification: NotificationIdentifier; lockedBy: string },
): Readonly<NotificationFormListState> {
    const identifier = FormListUtilities.generateListIdentifierFromNotification(props.notification);
    const formEntry = state[identifier];

    if (typeof formEntry === 'undefined') {
        throw new Error(
            `FormListReducer: loadFormXmlSuccessReducer: Could not find form entry for identifier '${identifier}'`,
        );
    }

    const oldEntryIndex = formEntry.entries.findIndex((entry) => FormUtilities.compare(props.form, entry.id));
    const oldEntry = formEntry.entries[oldEntryIndex];
    const lockedBy = FormUtilities.isFormUnlocked(oldEntry.submitStatus) ? '' : props.lockedBy;

    const newEntry: FormListEntryState = { ...oldEntry, lockedBy, cached: true };
    const newEntries = [...formEntry.entries];
    newEntries[oldEntryIndex] = newEntry;

    const notAllEntriesLoaded = newEntries.some((entry) => !entry.cached);

    return {
        ...state,
        [identifier]: {
            ...formEntry,
            entries: newEntries,
            downloadingAll: formEntry.downloadingAll && notAllEntriesLoaded,
        },
    };
}

function cleanupFormSuccessReducer(
    state: Readonly<NotificationFormListState>,
    props: { form: FormIdentifier; notification: NotificationIdentifier; cached: boolean },
): Readonly<NotificationFormListState> {
    const identifier = FormListUtilities.generateListIdentifierFromNotification(props.notification);
    const formEntry = state[identifier];

    if (typeof formEntry === 'undefined') {
        // it can happen that we only want to remove our files, without having the form list (and entries) loaded in state
        // then we don't need the reducer to do anything
        return state;
    }
    // If the passed form list is not the current one, we don't need to update anything
    if (
        !NotificationUtilities.compare(formEntry.notification, props.notification) ||
        formEntry.equipmentId !== props.form.equipmentId ||
        formEntry.materialId !== props.form.materialId
    ) {
        return state;
    }

    const oldEntry = formEntry.entries.findIndex((entry) => FormUtilities.compare(props.form, entry.id));
    const newEntry: FormListEntryState = {
        ...formEntry.entries[oldEntry],
        cached: props.cached,
        submitStatus: FormsConstants.SUBMIT_STATUS.UNKNOWN,
    };
    const newEntries = [...formEntry.entries];
    newEntries[oldEntry] = newEntry;

    return { ...state, [identifier]: { ...formEntry, entries: newEntries } };
}

function abortFormSuccessReducer(
    state: Readonly<NotificationFormListState>,
    props: { form: Readonly<FormIdentifier>; notification: Readonly<NotificationIdentifier> },
): Readonly<NotificationFormListState> {
    const identifier = FormListUtilities.generateListIdentifierFromNotification(props.notification);
    const formEntry = state[identifier];

    if (typeof formEntry === 'undefined') {
        throw new Error(
            `FormListReducer: abortFormSuccessReducer: Could not find form entry for identifier '${identifier}'`,
        );
    }

    const oldEntry = formEntry.entries.findIndex((entry) => FormUtilities.compare(props.form, entry.id));
    const newEntry: FormListEntryState = {
        ...formEntry.entries[oldEntry],
        lockedBy: '',
        submitStatus: SUBMIT_STATUS.ABORTED,
    };
    const newEntries = [...formEntry.entries];
    newEntries[oldEntry] = newEntry;

    return { ...state, [identifier]: { ...formEntry, entries: newEntries } };
}

function refreshXMLSuccessReducer(
    state: Readonly<NotificationFormListState>,
    props: {
        form: Readonly<FormIdentifier>;
        notification: Readonly<NotificationIdentifier>;
    },
): Readonly<NotificationFormListState> {
    const identifier = FormListUtilities.generateListIdentifierFromNotification(props.notification);
    const formEntry = state[identifier];

    if (typeof formEntry === 'undefined') {
        throw new Error(
            `FormListReducer: refreshXMLSuccessReducer: Could not find form entry for identifier '${identifier}'`,
        );
    }

    const oldEntry = formEntry.entries.findIndex((entry) => FormUtilities.compare(props.form, entry.id));
    const newEntry: FormListEntryState = {
        ...formEntry.entries[oldEntry],
        lockedBy: '',
        submitStatus: SUBMIT_STATUS.REFRESHED,
    };
    const newEntries = [...formEntry.entries];
    newEntries[oldEntry] = newEntry;

    return { ...state, [identifier]: { ...formEntry, entries: newEntries } };
}

function loadAllFormXmlReducer(
    state: Readonly<NotificationFormListState>,
    props: { notification: NotificationIdentifier },
): Readonly<NotificationFormListState> {
    const identifier = FormListUtilities.generateListIdentifierFromNotification(props.notification);
    const formEntry = state[identifier];

    if (typeof formEntry === 'undefined') {
        throw new Error(
            `FormListReducer: loadAllFormXmlReducer: Could not find form entry for identifier '${identifier}'`,
        );
    }

    return { ...state, [identifier]: { ...formEntry, downloadingAll: true } };
}

function submitFormSuccessReducer(
    state: Readonly<NotificationFormListState>,
    props: {
        notification: NotificationIdentifier;
        saveMode: FormsConstants.SUBMIT_STATUS;
        form: FormIdentifier;
        skipFormListProcessing: boolean;
    },
): Readonly<NotificationFormListState> {
    if (FormUtilities.getFormTypeFromId(props.form.formId) === FORMTYPE.REPLACEMENT_REPORT) {
        return { ...state };
    }
    if (props.skipFormListProcessing) {
        return { ...state };
    }
    const identifier = FormListUtilities.generateListIdentifierFromNotification(props.notification);
    const formEntry = state[identifier];

    if (typeof formEntry === 'undefined') {
        throw new Error(
            `FormListReducer: submitFormSuccessReducer: Could not find form entry for identifier '${identifier}'`,
        );
    }

    const newFormEntry: FormList = { ...formEntry, entries: [...formEntry.entries] };
    // update the affected form in the state
    const formListIndex = newFormEntry.entries.findIndex((entry) => FormUtilities.compare(entry.id, props.form));

    const formListEntryState = newFormEntry.entries[formListIndex];
    newFormEntry.entries[formListIndex] = { ...formListEntryState, submitStatus: props.saveMode };

    return { ...state, [identifier]: newFormEntry };
}

function cleanupFormListSuccessReducer(
    state: Readonly<NotificationFormListState>,
    props: { notification: NotificationIdentifier },
): Readonly<NotificationFormListState> {
    const identifier = FormListUtilities.generateListIdentifierFromNotification(props.notification);
    const newState = { ...state };

    delete newState[identifier];

    return newState;
}

function setSelectedLanguageReducer(
    state: Readonly<NotificationFormListState>,
    props: { notification: NotificationIdentifier; selectedLanguage: FormsConstants.FORM_LANGUAGES },
): Readonly<NotificationFormListState> {
    const identifier = FormListUtilities.generateListIdentifierFromNotification(props.notification);
    const formEntry = state[identifier];

    if (typeof formEntry === 'undefined') {
        throw new Error(
            `FormListReducer: submitFormSuccessReducer: Could not find form entry for identifier '${identifier}'`,
        );
    }

    const newFormEntry: FormList = { ...formEntry, selectedLanguage: props.selectedLanguage };

    return { ...state, [identifier]: newFormEntry };
}

const createFormListReducer = createReducer(
    initialNotificationFormListState,
    on(loadFormList, loadFormListReducer),
    on(loadFormListSuccess, loadFormListSuccessReducer),
    on(loadFormListError, loadFormListErrorReducer),
    on(loadEquipmentSetSuccess, loadEquipmentSetSuccessReducer),
    on(loadFormIntoCacheSuccess, loadFormIntoCacheSuccessReducer),
    on(cleanupFormSuccess, cleanupFormSuccessReducer),
    on(abortFormSuccess, abortFormSuccessReducer),
    on(refreshXMLSuccess, refreshXMLSuccessReducer),
    on(loadAllFormXml, loadAllFormXmlReducer),
    on(submitFormSuccess, submitFormSuccessReducer),
    on(cleanupFormListSuccess, cleanupFormListSuccessReducer),
    on(setSelectedLanguageInFormList, setSelectedLanguageReducer),
    on(handOverUnsubmittedFormsSuccess, () => initialNotificationFormListState),
);

export function formListsReducer(
    state: Readonly<NotificationFormListState>,
    action: Action,
): Readonly<NotificationFormListState> {
    return createFormListReducer(state, action);
}
